import React, {useEffect, useState, useRef, useMemo} from "react";
import { useIntl } from "react-intl";
import Button from "@ui-components/Button";
import Modal from "@ui-components/Modal";
import { FormattedMessage } from "react-intl";
import Input from "@ui-components/Input";
import BoardCard from "@components/BoardCard";
import ColorCollectionGrid from "@components/ColorCollectionGrid";
import { ArrowLeftIcon } from "ui-components/icons/ArrowLeftIcon";
import {ColorCollectionMiniGrid} from "@components/ColorCollectionMiniGrid";
import Spinner from "@ui-components/Spinner";


const KColorPickerModal = ({ color, onExit, setColor, boards, unselectableColors}) => {

  // hooks
  const intl = useIntl();

  // state
  const [search, setSearch] = useState("");
  const [selectedBoard, setSelectedBoard] = useState(undefined);
  const [internalColor, setInternalColor] = useState(
    // Color selected internally (not yet confirmed)
    undefined
  );

  // utils
  const getAvailableColors = (board) => {
    if (!board) return [];
    return board.colors
      .filter((c) => (
        !unselectableColors.includes(c.code)
        && (!search || c.code.startsWith("KK" + search))
      ))
      .map((c) => c.code);
  }
  const availableBoards = useMemo(
    // Board codes that have at least one available color (e.g. because of filtering or external constraints)
    () => boards?.filter((b) => getAvailableColors(b).length > 0) ?? [],
    [boards, unselectableColors, search]
  );
  const {searchedColorCount, totalColorCount} = useMemo(
    () => {
      if (!boards) return {
        searchedColorCount: undefined, totalColorCount: undefined
      };
      const all = boards.flatMap((b) => b.colors)
        .filter((c) => !unselectableColors.includes(c.code));
      const searched = all.filter((c) => !search || c.code.startsWith("KK" + search));
      return {searchedColorCount: searched.length, totalColorCount: all.length};
    },
    [boards, unselectableColors, search]
  );

  // refs
  const gridRef = useRef(null);

  // handlers
  useEffect(() => {
    // Whenever boards get loaded, or if a color was pre-selected, set the selectedBoard/selectedColor accordingly
    if (!boards || !color) return;
    setSelectedBoard(boards.find((b) => b.colors.some((c) => c.code === color.code)));
    setInternalColor(color.code);
  }, [boards, color]);

  // define modal content
  let content;
  if (!boards)
    // ===== CASE A: boards not loaded yet - show spinner
    content = (
      <div className="relative w-full h-40">
        <Spinner styleType="white"/>
      </div>
    )
  else if (!selectedBoard)
    // ===== CASE B: no board selected yet - show available boards and search control
    content = <>
      {/* ----- Search input with KK prefix */}
      <h3 className="text-lg font-semibold">
        {intl.formatMessage({
          id: "search_by_code",
          defaultMessage: "Ricerca per codice",
        })}
      </h3>
      <div className="flex flex-row items-center gap-2">
        <div className="font-medium">KK</div>
        <div className="flex-1">
          <Input
            value={search}
            onChange={async (e) => {
              e.preventDefault();
              setSearch(e.target.value);
            }}
            hideBottomMargin={true}
            withMargin={false}
            placeholder={intl.formatMessage({
              id: "enter_code_placeholder",
              defaultMessage: "Inserisci il codice colore",
            })}
            autoComplete="off"
            autoFocus
          />
        </div>
        <div className="mt-1">
          <Button
            onClick={() => {
              const firstMatch = boards.flatMap((b) => b.colors).find((c) => c.code === "KK" + search || c.code === "KK" + " " + search);
              if (!firstMatch) return;
              const board = boards.find((b) => b.colors.some((c) => c.code === firstMatch.code));
              if (!board) return;
              setSearch("");
              setSelectedBoard(board);
              setInternalColor(firstMatch.code);
            }}
            disabled={
              !search
              || boards.flatMap((b) => b.colors).filter((c) => c.code === "KK" + search).length === 0
            }
          >
            <FormattedMessage id="go_cta" defaultMessage="Vai" />
          </Button>
        </div>
        {searchedColorCount !== undefined && searchedColorCount !== totalColorCount && (
          <div
            className="flex flex-col items-center justify-center font-light leading-tight uppercase"
            style={{fontSize: "0.55rem"}}
          >
            <span>{searchedColorCount}/{totalColorCount}</span>
            <span><FormattedMessage id="results" defaultMessage="Risultati"/></span>
          </div>
        )}
      </div>
      <hr className="border-dashed my-2"/>
      {/* ----- Boards grid */}
      <h3 className="text-lg font-semibold">
        {intl.formatMessage({
          id: "surf_color_boards",
          defaultMessage: "Naviga per gruppo colore",
        })}
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-1">
        {[
          // This is the actual order of boards, which doesn't reflect the one in the DB. This is the correct one
          // since the images have a fixed order (e.g. 1/8, 2/8, ...)
          "Neutral", "Blue", "Green", "Brown", "Yellow", "Terracotta", "Magenta", "Purple",
        ].map((name) => {
          const board = boards.find((b) => b.cod_board === name);
          if (!board)
            return null;
          const active = availableBoards.some((b) => b.cod_board === board.cod_board);
          return (
            <BoardCard
              key={board.cod_board}
              onClick={() => {
                setSearch("");
                setSelectedBoard(board);
              }}
              board={board}
              disabled={!active}
            />
          );
        })}
      </div>
    </>
  else
    // ===== CASE C: board selected - show colors in that board
    content = <>
      {/* ----- Back button and selected board info */}
      <div className="flex flex-row px-12 sm:px-20 py-1.5 justify-between items-center">
        <div
          className="flex flex-col gap-1 items-center justify-center cursor-pointer text-xs font-medium"
          onClick={() => setSelectedBoard(undefined)}
        >
          <ArrowLeftIcon />
          <FormattedMessage id="back" defaultMessage="Indietro"/>
        </div>
        <div className="flex flex-col gap-1 items-center justify-center">
          <div
            className={`w-14 h-8`}
            style={{backgroundColor: selectedBoard.val_hex}}
          />
          <span className="text-xs font-medium">
            {selectedBoard.des_board}
          </span>
        </div>
      </div>
      <hr className="border-dashed" />
      {/* ----- Colors grid */}
      <div
        ref={gridRef}
        className="overflow-auto mt-2 pb-4 relative"
        style={{maxHeight: "calc(100vh - 420px)"}}
      >
        <ColorCollectionGrid
          colors={selectedBoard.colors}
          availableColors={getAvailableColors(selectedBoard)}
          selected={internalColor}
          onColorClick={(code) => {
            // If the user clicks on the already selected color, unselect it; otherwise, select the clicked color
            setInternalColor((prev) => prev === code ? undefined : code);
          }}
          containerStyle={{minWidth: "850px", width: "fit-content"}}
          intensityGroups={[
            ["LIGHT", "MID", "DEEP"],
            ["BRIGHT"],
          ]}
          intensityGroupsBgColor="bg-gray-100"
          intensityGroupsExtraCls={{
            // NOTE: this is done to align the "bright" group with the "mid" one. It only works if there are 3
            // rows in the "light" group; if this assumption changes, this needs to be updated
            "BRIGHT": "mt-11",
          }}
        />
      </div>
    </>

  return <>
    <Modal large onExit={onExit}>
      <h1 className="text-center text-2xl font-extrabold">
        {intl.formatMessage({id: "select_kcolor_color"})}
      </h1>
      <p className="py-5">
        {intl.formatMessage({id: "select_kcolor_color_helper"})}
      </p>
      <div className="bg-gray-100 p-4 rounded-xl shadow flex flex-col gap-4">
        {content}
      </div>
      {/* ----- Footer with Cancel/Confirm buttons (only if a color is selected) */}
      {!!selectedBoard && (
        <div className="w-full mt-4">
          <Button
            full
            onClick={() => {
              if (!internalColor || !boards) return;
              const selected = boards.flatMap((b) => b.colors).find((c) => c.code === internalColor);
              if (!selected) return;
              setColor(selected);
              onExit();
            }}
            disabled={!internalColor || !selectedBoard.colors.some((c) => c.code === internalColor)}
          >
            <FormattedMessage id="confirm"/>
          </Button>
        </div>
      )}
    </Modal>
    {!!selectedBoard && (
      <ColorCollectionMiniGrid
        colors={selectedBoard.colors}
        intensityGroups={[["LIGHT", "MID", "DEEP"], ["BRIGHT"]]}
        intensityGroupsExtraCls={{
          // NOTE: this is done to align the "bright" group with the "mid" one. It only works if there are 3
          // rows in the "light" group; if this assumption changes, this needs to be updated
          "BRIGHT": "mt-2",
        }}
        gridRef={gridRef}
      />
    )}
  </>
};

export default KColorPickerModal;
